import { Section } from "../../../components/StructureElements/Section";
import { SectionHeaderTypography } from "../../../components/Typographies/SectionHeaderTypography";
import React, { useCallback, useState } from "react";
import { SectionContent } from "../SectionContent";
import { SectionHeader } from "../SectionHeader";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import { InputLabel } from "../../../components/Input/InputLabel";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { ExcludedEmailDomainDto } from "../../../../data/models/responses/ExcludedEmailDomainDto";
import { CustomInput } from "../../../components/Input/CustomInput";

export interface ExcludedDomainsEditorViewModel {
  disabled: boolean;
  initialState: boolean;
  onToggle: () => void;
  excludedDomains: ExcludedEmailDomainDto[];
  onAddDomain: (domain: string) => void;
  onDeleteDomain: (domain: string) => void;
  isLoading: boolean;
}

export function ExcludedDomainsEditor({
  disabled,
  initialState,
  onToggle,
  excludedDomains,
  onAddDomain,
  onDeleteDomain,
  isLoading,
}: ExcludedDomainsEditorViewModel) {
  const [newDomain, setNewDomain] = useState("");

  const handleAddDomain = useCallback(() => {
    if (newDomain.trim()) {
      onAddDomain(newDomain.trim());
      setNewDomain("");
    }
  }, [newDomain, onAddDomain]);

  const handleDeleteDomain = useCallback(
    (domain: string) => {
      onDeleteDomain(domain);
    },
    [onDeleteDomain]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleAddDomain();
      }
    },
    [handleAddDomain]
  );

  return (
    <Section>
      <SectionHeader
        isCloseable
        initialState={initialState}
        onToggle={onToggle}
      >
        <SectionHeaderTypography>Excluded domains</SectionHeaderTypography>
      </SectionHeader>
      <SectionContent close={!initialState}>
        <StrucRow className="flex items-center gap-x-4">
          <StrucCol size={6}>
            <InputWrapper>
              <InputLabel>
                Add new domain to exclude from recieving emails
              </InputLabel>
              <CustomInput
                type="text"
                value={newDomain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewDomain(e.target.value)
                }
                onKeyPress={handleKeyPress}
                placeholder="jetsport.dk"
                disabled={disabled}
              />
            </InputWrapper>
          </StrucCol>
          <StrucCol size={2}>
            <PrimaryButton
              onClick={handleAddDomain}
              disabled={disabled || !newDomain.trim()}
            >
              Add Domain
            </PrimaryButton>
          </StrucCol>
        </StrucRow>

        {excludedDomains.length > 0 && (
          <StrucRow>
            <StrucCol size={6}>
              <div className="space-y-2">
                <InputLabel>Excluded domains</InputLabel>
                <div className="flex flex-col gap-y-2 rounded-md px-4 py-2 max-h-60 overflow-y-auto">
                  {excludedDomains.map((domain) => (
                    <div key={domain.id} className="flex gap-x-4 items-center">
                      <span className="text-sm">{domain.domain}</span>
                      <SecondaryButton
                        onClick={() => handleDeleteDomain(domain.domain)}
                        disabled={disabled}
                        className="text-xs"
                      >
                        Remove
                      </SecondaryButton>
                    </div>
                  ))}
                </div>
              </div>
            </StrucCol>
          </StrucRow>
        )}
      </SectionContent>
    </Section>
  );
}
